import { useMemo, useState, useEffect } from 'react';
import {
  VizCard,
  NowYouTry,
  usePrefersReducedMotion,
  cueIsActive,
  type VizCue,
} from '../vizShared';

// SigFigs — visualizes significant figures in a measured number: each digit is
// coloured significant (accent) or not-significant (dimmed), with a live count
// and the rule that applies. Teaches leading/captive/trailing-zero rules.
// config: { number?: string|number, numbers?: Array<string|number>, rule?: string, title? }
// Cue: cue.step walks the digits left→right; cue.progress reveals proportionally.

export interface SigFigsConfig {
  number?: string | number;
  numbers?: Array<string | number>;
  rule?: string;
  title?: string;
  cue_steps?: unknown;
}

interface Props {
  config?: SigFigsConfig;
  onComplete?: (result?: unknown) => void;
  cue?: VizCue;
}

interface DigitInfo { ch: string; isDigit: boolean; sig: boolean; reason: string }

// Compute per-character significance for a numeric string.
function analyze(raw: string): { chars: DigitInfo[]; count: number; hasDecimal: boolean } {
  const s = String(raw).trim();
  const body = s.replace(/^[-+]/, '');
  const hasDecimal = body.includes('.');
  // indices of digit chars within body
  const digitIdx: number[] = [];
  for (let i = 0; i < body.length; i++) if (/[0-9]/.test(body[i])) digitIdx.push(i);
  const digits = digitIdx.map((i) => body[i]);
  const firstNonZero = digits.findIndex((c) => c !== '0');
  let lastNonZero = -1;
  for (let i = digits.length - 1; i >= 0; i--) if (digits[i] !== '0') { lastNonZero = i; break; }

  const sigByDigit = digits.map((c, i) => {
    if (firstNonZero === -1) return { sig: false, reason: 'all zeros — ambiguous' };
    if (i < firstNonZero) return { sig: false, reason: 'leading zero (never significant)' };
    if (i > lastNonZero) return { sig: hasDecimal, reason: hasDecimal ? 'trailing zero after a decimal (significant)' : 'trailing zero, no decimal (not significant)' };
    if (c === '0') return { sig: true, reason: 'captive zero between non-zeros (significant)' };
    return { sig: true, reason: 'non-zero digit (always significant)' };
  });

  // map back to every char in body
  let d = 0;
  const chars: DigitInfo[] = body.split('').map((ch) => {
    if (/[0-9]/.test(ch)) { const info = sigByDigit[d++]; return { ch, isDigit: true, sig: info.sig, reason: info.reason }; }
    return { ch, isDigit: false, sig: false, reason: ch === '.' ? 'decimal point' : '' };
  });
  const count = sigByDigit.filter((x) => x.sig).length;
  return { chars, count, hasDecimal };
}

export default function SigFigs({ config = {}, onComplete, cue }: Props) {
  const reduced = usePrefersReducedMotion();
  const hasCue = cueIsActive(cue);
  const list = useMemo<string[]>(() => {
    if (Array.isArray(config.numbers) && config.numbers.length) return config.numbers.map(String);
    return [String(config.number ?? '0.04060')];
  }, [config.number, config.numbers]);

  const analyses = useMemo(() => list.map(analyze), [list]);

  // The "active" number when several are shown (cue.step can advance it).
  const [activeNum, setActiveNum] = useState(0);
  // How many digit-positions are "revealed"/walked for the active number.
  const [revealed, setRevealed] = useState<number>(reduced || hasCue ? 99 : 0);
  const [showTry, setShowTry] = useState(false);
  const [interacted, setInteracted] = useState(false);

  // Self-demo: walk the digits once on mount (unless reduced/cue-driven).
  useEffect(() => {
    if (reduced || hasCue) { setRevealed(99); return; }
    setRevealed(0);
    const active = analyses[0] || { chars: [] };
    const timers: number[] = [];
    active.chars.forEach((_, i) => timers.push(window.setTimeout(() => setRevealed(i + 1), 220 * (i + 1))));
    timers.push(window.setTimeout(() => { setRevealed(99); setShowTry(true); }, 220 * (active.chars.length + 1)));
    return () => timers.forEach((t) => window.clearTimeout(t));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Cue: progress reveals proportionally; step selects which number (multi) and reveals fully.
  useEffect(() => {
    if (!cue) return;
    setShowTry(false);
    if (typeof cue.step === 'number' && list.length > 1) {
      setActiveNum(Math.max(0, Math.min(list.length - 1, Math.round(cue.step))));
      setRevealed(99);
    } else if (typeof cue.progress === 'number') {
      const a = analyses[activeNum] || { chars: [] };
      setRevealed(Math.round(Math.max(0, Math.min(1, cue.progress)) * a.chars.length));
    }
  }, [cue, list.length, analyses, activeNum]);

  const a = analyses[activeNum] || analyses[0];

  const markInteracted = (i: number) => {
    setActiveNum(i); setRevealed(99); setShowTry(false);
    if (!interacted) { setInteracted(true); onComplete?.({ type: 'sig_figs', number: list[i], count: analyses[i].count }); }
  };

  return (
    <VizCard
      eyebrow="Measurement · Significant figures"
      title={config.title ?? 'Which digits are significant?'}
      subtitle="Coloured = significant · dimmed = not significant"
      reducedMotion={reduced}
      hint={list.length > 1 ? 'Click a number to analyze it' : 'Each digit is checked against the sig-fig rules'}
    >
      <div className="relative flex w-full flex-col gap-3">
        <NowYouTry show={showTry && !reduced && !hasCue} />

        {/* multi-number selector */}
        {list.length > 1 && (
          <div className="flex flex-wrap gap-1.5">
            {list.map((n, i) => (
              <button
                key={i}
                type="button"
                onClick={() => markInteracted(i)}
                className={`rounded-sm border px-2 py-1 font-mono text-xs transition-colors ${
                  i === activeNum ? 'border-ws-accent bg-ws-accent text-ws-on-accent' : 'border-ws-border bg-ws-tile text-ws-ink-2 hover:bg-ws-surface-2'
                }`}
              >
                {n} <span className="opacity-70">({analyses[i].count})</span>
              </button>
            ))}
          </div>
        )}

        {/* the number, digit by digit */}
        <div className="grid place-items-center rounded-md border border-ws-border bg-ws-surface-2 px-4 py-8">
          <div className="flex items-end font-mono text-5xl font-bold tracking-wide">
            {a.chars.map((c, i) => {
              const shown = i < revealed || revealed >= 99;
              const lit = c.isDigit && c.sig;
              return (
                <span
                  key={i}
                  title={c.reason}
                  style={{ transition: reduced ? undefined : 'color 200ms, opacity 200ms' }}
                  className={
                    !shown ? 'text-ws-ink-4 opacity-30'
                      : lit ? 'text-ws-accent'
                      : c.isDigit ? 'text-ws-ink-4 line-through decoration-2'
                      : 'text-ws-ink-2'
                  }
                >
                  {c.ch}
                </span>
              );
            })}
          </div>
          <div className="mt-4 text-sm text-ws-ink-2">
            <span className="font-semibold text-ws-accent">{a.count}</span> significant figure{a.count === 1 ? '' : 's'}
          </div>
        </div>

        {/* the rule that applies (from the last revealed digit, or config.rule) */}
        <p className="min-h-[1.25rem] text-center text-xs text-ws-ink-3">
          {config.rule
            ?? (a.chars.filter((c, i) => c.isDigit && (i < revealed || revealed >= 99)).slice(-1)[0]?.reason ?? 'Non-zero digits are always significant.')}
        </p>
      </div>
    </VizCard>
  );
}
